import React, { useState, useEffect, useContext } from "react";
import {
  Label,
  Form,
  FormGroup,
  Row,
  Col,
  InputGroup,
} from "reactstrap";
import { useController, useForm } from "react-hook-form";
import {
  getCarTypes,
  showAlert,
  getUserDetails,
} from "../../Services/Shared.service";
import { getTaxGroupList, createPackagesBulk } from "../../Services/Packages.service";
import { createCorporateClient, updateCorporateClient } from "../../Services/CorporateClient.service";
import SharedDataContext from "../../_helper/SharedData";
import Select from "react-select";
import { Box, Checkbox, Typography, Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions, Button } from "@mui/material";
import CombinationTable from "./CombinationTable";
import DatePicker from "react-datepicker";
import { ArrowDown, ArrowUp } from "react-feather";
import { serviceTypeOptions, serviceTypeMap } from "../../Constant/serviceTypeConstants";
import { addMonths } from "date-fns";
import PackageRateCreateForm from "./PackageRateCreateForm";
import DriverPayoutsTable from "../ViewTariff/Components/Tables/DriverPayoutsTable";
import DriverCreditsTable from "../ViewTariff/Components/Tables/DriverCreditsTable";
import { usePayoutAllowedCities } from "../../hooks/usePayoutAllowedCities";

function CorpBulkCreate({ handleCloseModal, registerCloseHandler }) {
  const [activeTab, setActiveTab] = useState(0); // 0 = Create Client, 1 = Bulk Create
  const [createdClients, setCreatedClients] = useState([]);
  const [isCreatingClient, setIsCreatingClient] = useState(false);
  const [draftRates, setDraftRates] = useState(null);
  
  // Warning dialog states
  const [showClientWarning, setShowClientWarning] = useState(false);
  const [clientsWithoutPackages, setClientsWithoutPackages] = useState([]);
  const [showCloseWarning, setShowCloseWarning] = useState(false);
  const [skipClientWarning, setSkipClientWarning] = useState(false);
  const [hasVisitedTab3, setHasVisitedTab3] = useState(false);
  const [globalCityPreselected, setGlobalCityPreselected] = useState(false);
  const [enableDriverPayout, setEnableDriverPayout] = useState(false);
  const [enableDriverCredit, setEnableDriverCredit] = useState(false);
  
  // Tab 1 - Client Form
  const clientForm = useForm({
    defaultValues: {
      companyName: "",
      serviceCities: [], // Multi-select cities for initial creation
      serviceCity: null, // Single-select city for editing
      contactPerson: "",
      mobile: "",
      email: "",
      addressLine: "",
      city: "",
      state: "",
      pincode: "",
      overallTax: null,
      // Advanced Configuration fields
      panNo: "",
      serviceTaxNo: "",
      fax: "",
      billingAddress: "",
      billingCity: "",
      billingState: "",
      billingPincode: "",
      billingContactPerson: "",
      billingContactMobile: "",
    },
  });

  // Tab 2 - Package Form
  const packageForm = useForm({
    defaultValues: {
      selectedClient: null,
      serviceType: null,
      packageName: "",
      description: "",
      carTypes: [],
      terminals: [],
      applicableFromDate: new Date(),
      applicableToDate: addMonths(new Date(), 1),
    },
  });

  const { cityData, globalCityField } = useContext(SharedDataContext);
  const [combinedTax, setCombinedTax] = useState([]);
  const [carTypeData, setCarTypeData] = useState([]);
  const [combinations, setCombinations] = useState([]);
  const [isAdvanced, setIsAdvanced] = useState(false);

  const terminals = [
    { value: "IGI-T3", label: "IGI-T3" },
    { value: "IGI-T1", label: "IGI-T1" },
  ];

  // Tax group fetch
  useEffect(() => {
    getTaxGroupList().then((result) => {
      if (result.status === 200) {
        setCombinedTax(generateTaxCombinationsForSelectBox(result.data));
      }
    });
  }, []);

  // Preselect city from global city context (create mode only)
  useEffect(() => {
    if (cityData.length === 0 || createdClients.length > 0) return;
    const globalCity = globalCityField?.value;
    if (!globalCity?.id) return;
    const currentCities = clientForm.getValues("serviceCities");
    if (currentCities && currentCities.length > 0) return;
    const matchingOption = cityData.find((c) => c.value?.id === globalCity.id);
    if (matchingOption) {
      serviceCitiesField.onChange([matchingOption]);
      setGlobalCityPreselected(true);
    }
  }, [cityData]);

  function generateTaxCombinationsForSelectBox(taxData) {
    let basezero = taxData.filter((t) => t.taxRate === 0);
    let basenonzero = taxData.filter((t) => t.taxRate !== 0);
    basezero.sort((a, b) => a.taxCategory.localeCompare(b.taxCategory));
    basenonzero.sort((a, b) => a.taxCategory.localeCompare(b.taxCategory));
    let combinationsArr = [];
    if (basenonzero.length >= 2) {
      combinationsArr.push({
        label: `Base ${basenonzero[0].taxRate}% Service ${basenonzero[1].taxRate}%`,
        value: { baseTax: basenonzero[0], serviceTax: basenonzero[1] },
      });
    }
    if (basezero.length >= 2) {
      combinationsArr.push({
        label: `Base ${basezero[0].taxRate}% Service ${basezero[1].taxRate}%`,
        value: { baseTax: basezero[0], serviceTax: basezero[1] },
      });
    }
    return combinationsArr;
  }

  // ===== TAB 1: CLIENT FORM CONTROLLERS =====
  const serviceCitiesField = useController({
    name: "serviceCities",
    control: clientForm.control,
    rules: {
      validate: (value) => (value && value.length > 0) || "At least one Service City is required",
    },
  }).field;

  const mobileField = useController({
    name: "mobile",
    control: clientForm.control,
    rules: { required: { value: true, message: "Mobile Number is Required" } },
  }).field;

  const handleCitiesChange = (selectedOptions) => {
    serviceCitiesField.onChange(selectedOptions || []);
  };

  // Handler for single city select (edit mode)
  const handleSingleCityChange = (selectedOption) => {
    clientForm.setValue("serviceCity", selectedOption?.value || null);
  };

  // Load client data in edit mode when city + service type is selected
  const loadClientData = (client) => {
    if (!client) return;
    
    clientForm.setValue("companyName", client.companyName || "");
    clientForm.setValue("contactPerson", client.contactPerson || "");
    clientForm.setValue("mobile", client.mobile || "");
    clientForm.setValue("email", client.email || "");
    clientForm.setValue("addressLine", client.addressLine || "");
    clientForm.setValue("city", client.city || "");
    clientForm.setValue("state", client.state || "");
    clientForm.setValue("pincode", client.pincode || "");
    clientForm.setValue("panNo", client.panNo || "");
    clientForm.setValue("serviceTaxNo", client.serviceTaxNo || "");
    clientForm.setValue("fax", client.fax || "");
    clientForm.setValue("billingAddress", client.billingAddress || "");
    clientForm.setValue("billingCity", client.billingCity || "");
    clientForm.setValue("billingState", client.billingState || "");
    clientForm.setValue("billingPincode", client.billingPincode || "");
    clientForm.setValue("billingContactPerson", client.billingContactPerson || "");
    clientForm.setValue("billingContactMobile", client.billingContactMobile || "");
    
    // Find matching tax option
    if (client.baseFareTaxId && combinedTax.length > 0) {
      const matchingTax = combinedTax.find(
        (tax) => tax.value?.baseTax?.taxGroupId === client.baseFareTaxId
      );
      if (matchingTax) {
        clientForm.setValue("overallTax", matchingTax);
      }
    }
  };

  // Watch for city changes in edit mode to load data
  const watchedCity = clientForm.watch("serviceCity");

  // Auto-select first city when entering edit mode (Tab 1)
  useEffect(() => {
    if (activeTab === 0 && createdClients.length > 0 && cityData.length > 0 && !clientForm.getValues("serviceCity")) {
      // Get first city from created clients
      const firstClient = createdClients[0];
      const cityOption = cityData.find((c) => c.value?.id === firstClient.serviceCityId);
      if (cityOption) {
        clientForm.setValue("serviceCity", cityOption.value);
      }
    }
  }, [activeTab, createdClients.length, cityData.length]);

  // Load client data when city changes in edit mode
  useEffect(() => {
    if (createdClients.length === 0 || !watchedCity) return;
    
    // Find matching client for this city
    const matchingClient = createdClients.find(
      (c) => c.serviceCityId === watchedCity.id
    );
    
    if (matchingClient) {
      loadClientData(matchingClient);
    }
  }, [watchedCity?.id, createdClients.length]);

  const overallTaxField = useController({
    name: "overallTax",
    control: clientForm.control,
    rules: {
      required: { value: true, message: "Tax is required" },
    },
  }).field;

  const handleTaxChange = (selectedOption) => {
    overallTaxField.onChange(selectedOption);
  };

  // Build client API body - now accepts city as parameter for multi-city support
  const buildClientBody = (formValues, city, existingClientId = null) => {
    const userDetails = getUserDetails();
    const body = {
      sapGlCode: null,
      isMegaCabsCompany: false,
      companyName: formValues.companyName || null,
      companyLogo: null,
      cinNo: null,
      panNo: formValues.panNo || null,
      serviceTaxNo: formValues.serviceTaxNo || null,
      contactPerson: formValues.contactPerson || null,
      mobile: formValues.mobile || null,
      email: formValues.email || null,
      addressLine: formValues.addressLine || null,
      city: formValues.city || null,
      state: formValues.state || null,
      pincode: formValues.pincode || null,
      phone1: null,
      phone2: null,
      fax: formValues.fax || null,
      billingAddress: formValues.billingAddress || null,
      billingCity: formValues.billingCity || null,
      billingState: formValues.billingState || null,
      billingPincode: formValues.billingPincode || null,
      billingContactPerson: formValues.billingContactPerson || null,
      billingContactMobile: formValues.billingContactMobile || null,
      createdById: userDetails.userId,
      createdViaType: null,
      deletedOn: null,
      isDeleted: false,
      deletedById: null,
      serviceCityId: city?.id || null,
      discountValue: null,
      discountType: null,
      baseFareTaxId: formValues.overallTax?.value?.baseTax?.taxGroupId || null,
      serviceTaxId: formValues.overallTax?.value?.serviceTax?.taxGroupId || null,
      isAirportClient: false,
      lutNo: null,
      serviceDescription: city?.serviceDescription || null,
      sacCode: city?.sacCode || null,
      isSendSms: true,
      isSendMail: null,
      isStopInvoice: null,
      a2c: false,
      c2a: false,
      airportCharge: null,
      airportCharge: null,
      isWalletEnabled: true,
    };
    // If updating existing client, add the ID
    if (existingClientId) {
      body.id = existingClientId;
    }
    return body;
  };

  // Tab 1 Submit - Create or Update clients
  const handleClientSubmit = async (formValues) => {
    const { serviceCities, serviceCity } = formValues;
    const isEditMode = createdClients.length > 0;

    // In edit mode, use single city; in create mode, use multi-city
    if (isEditMode) {
      if (!serviceCity) {
        showAlert("warning", "Please select a service city");
        return;
      }
    } else {
      if (!serviceCities || serviceCities.length === 0) {
        showAlert("warning", "Please select at least one service city");
        return;
      }
    }

    setIsCreatingClient(true);

    try {
      if (isEditMode) {
        // EDIT MODE: Single city - update or create for that city
        const city = serviceCity;
        const existingClient = createdClients.find(
          (c) => c.serviceCityId === city.id
        );
        const body = buildClientBody(formValues, city, existingClient?.id);
        let resp;
        if (existingClient?.id) {
          resp = await updateCorporateClient(body);
        } else {
          resp = await createCorporateClient(body);
        }
        const updatedClient = {
          ...resp.data,
          serviceCityName: city.name || city.cityName,
        };

        // Merge: keep all other clients, replace/add the one being edited
        const otherClients = createdClients.filter((c) => c.serviceCityId !== city.id);
        const mergedClients = [...otherClients, updatedClient];
        setCreatedClients(mergedClients);

        if (existingClient?.id) {
          showAlert("success", `Updated client for ${city.name || city.cityName}!`);
        } else {
          showAlert("success", `Created client for ${city.name || city.cityName}!`);
        }
      } else {
        // CREATE MODE: Multi-city - create one client per city
        const updatedClients = [];

        for (const cityOption of serviceCities) {
          const city = cityOption.value;
          const body = buildClientBody(formValues, city, null);
          const resp = await createCorporateClient(body);
          updatedClients.push({
            ...resp.data,
            serviceCityName: city.name || city.cityName,
          });
        }

        setCreatedClients(updatedClients);
        showAlert("success", `Created ${updatedClients.length} client(s) successfully!`);
        setActiveTab(1); // Only auto-navigate on initial creation
      }
    } catch (error) {
      showAlert("error", error.response?.data?.message || "Failed to process corporate client");
    } finally {
      setIsCreatingClient(false);
    }
  };

  // ===== TAB 2: PACKAGE FORM CONTROLLERS =====
  const selectedClientField = useController({
    name: "selectedClient",
    control: packageForm.control,
    rules: { required: { value: true, message: "Please select a client" } },
  }).field;

  const serviceTypeField = useController({
    name: "serviceType",
    control: packageForm.control,
    rules: { required: { value: true, message: "Please select a service type" } },
  }).field;

  const carTypesField = useController({
    name: "carTypes",
    control: packageForm.control,
    rules: {
      validate: (value) => (value && value.length > 0) || "At least one Car Type is required",
    },
  }).field;

  const terminalsField = useController({
    name: "terminals",
    control: packageForm.control,
    rules: {
      validate: (value) => {
        const client = packageForm.getValues("selectedClient");
        const serviceType = packageForm.getValues("serviceType");
        // Show terminals for Delhi + A2C service type
        if (client?.serviceCityId === 1 && serviceType?.value === "A2C") {
          return (value && value.length > 0) || "At least one Terminal is required";
        }
        return true;
      },
    },
  }).field;

  const applicableFromDateField = useController({
    name: "applicableFromDate",
    control: packageForm.control,
    rules: { required: { value: true, message: "Applicable From Date is Required" } },
  }).field;

  const applicableToDateField = useController({
    name: "applicableToDate",
    control: packageForm.control,
    rules: {
      required: { value: true, message: "Applicable To Date is Required" },
      validate: (value) => {
        const from = packageForm.getValues("applicableFromDate");
        if (!from || !value) return true;
        return value > from || "Applicable To must be greater than Applicable From";
      },
    },
  }).field;

  const watchSelectedClient = packageForm.watch("selectedClient");
  const watchServiceType = packageForm.watch("serviceType");
  // C2A and C2C are isAirportExpress: true, A2C is false
  const isAirportExpress = watchServiceType?.value === "C2A" || watchServiceType?.value === "C2C" || watchServiceType?.value === "ALL";
  // Show terminals for Delhi + A2C service type
  const showTerminals = watchSelectedClient?.serviceCityId === 1 && watchServiceType?.value === "A2C";

  // Fetch car types when selected client or service type changes
  useEffect(() => {
    const serviceCityId = watchSelectedClient?.serviceCityId;
    const serviceTypeValue = watchServiceType?.value;
    if (!serviceCityId || !serviceTypeValue) {
      setCarTypeData([]);
      return;
    }
    getCarTypes({ serviceCityId, travelType: serviceTypeValue })
      .then((result) => {
        const data = result.data.map((element) => ({
          label: element.name,
          value: element,
        }));
        setCarTypeData(data);
      })
      .catch((err) => {
        showAlert("error", "Unable to fetch Car details");
      });
  }, [watchSelectedClient, watchServiceType]);

  // Clear dependent fields when client or service type changes
  useEffect(() => {
    packageForm.setValue("carTypes", []);
    packageForm.setValue("terminals", []);
  }, [watchSelectedClient, watchServiceType]);

  const handleClientSelect = (selectedOption) => {
    selectedClientField.onChange(selectedOption?.value || null);
  };

  const handleServiceTypeSelect = (selectedOption) => {
    serviceTypeField.onChange(selectedOption || null);
  };

  // Sync package names if client details change
  useEffect(() => {
    setCombinations((prevCombinations) => {
      let hasChanges = false;
      const updated = prevCombinations.map((pkg) => {
        const client = createdClients.find((c) => c.id === pkg.corporateClientId);
        // Check if client exists and name has changed
        if (client && client.companyName !== pkg.corporateClientName) {
          hasChanges = true;
          const oldNamePrefix = `${pkg.corporateClientName}-`;
          const newNamePrefix = `${client.companyName}-`;
          
          let newPkgName = pkg.name;
          if (newPkgName.startsWith(oldNamePrefix)) {
            newPkgName = newNamePrefix + newPkgName.substring(oldNamePrefix.length);
          } else {
             newPkgName = newPkgName.replace(pkg.corporateClientName, client.companyName);
          }

          return {
            ...pkg,
            corporateClientName: client.companyName,
            name: newPkgName
          };
        }
        return pkg;
      });
      
      return hasChanges ? updated : prevCombinations;
    });
  }, [createdClients]);

  // Helper to create package DTO
  const createPackageDTO = (carType, terminalValue = null) => {
    const serviceTypeValue = packageForm.getValues("serviceType")?.value || "";
    return {
      id: Date.now() + Math.random(),
      name: `${packageForm.getValues("packageName")}-${packageForm.getValues("selectedClient").serviceCityName}-${serviceTypeValue}-${carType.value.name}${terminalValue ? `-${terminalValue}` : ""}`,
      description: packageForm.getValues("description") || "",
      isActive: true,
      isKiosk: false,
      autoRollover: false,
      isAirportExpress: isAirportExpress,
      isAirportBooking: false,
      serviceCityId: watchSelectedClient.serviceCityId,
      serviceCityName: watchSelectedClient.serviceCityName || "",
      baseFareTaxId: watchSelectedClient.baseFareTaxId || null,
      baseFareTaxName: "",
      serviceTaxId: watchSelectedClient.serviceTaxId || null,
      serviceTaxName: "",
      corporateClientId: watchSelectedClient.id,
      corporateClientName: watchSelectedClient.companyName,
      thirdPartyClientId: null,
      applicableFromDate: new Date(packageForm.getValues("applicableFromDate")),
      applicableToDate: new Date(packageForm.getValues("applicableToDate")),
      carTypeId: carType.value.id,
      carTypeName: carType.value.name,
      terminal: terminalValue,
      isDriverPayout: false,
      initialDistanceKm: null,
      perKmCharge: null,
      initialDistanceCharge: null,
      tollAmount: null,
      // Service type for packageRideType mapping in rate creation
      serviceType: serviceTypeMap[serviceTypeValue],
    };
  };

  // Add combinations
  const addCombinations = (formValues) => {
    const { carTypes, terminals: selectedTerminals } = formValues;
    const newCombinations = [];

    if (showTerminals && selectedTerminals.length > 0) {
      carTypes.forEach((carType) => {
        selectedTerminals.forEach((terminal) => {
          newCombinations.push(createPackageDTO(carType, terminal.value));
        });
      });
    } else {
      carTypes.forEach((carType) => {
        newCombinations.push(createPackageDTO(carType));
      });
    }

    const filteredCombinations = newCombinations.filter((newPkg) => {
      const isDuplicate = combinations.some(
        (existing) =>
          existing.serviceCityId === newPkg.serviceCityId &&
          existing.carTypeId === newPkg.carTypeId &&
          existing.terminal === newPkg.terminal &&
          existing.isAirportExpress === newPkg.isAirportExpress &&
          existing.corporateClientId === newPkg.corporateClientId &&
          existing.name === newPkg.name
      );
      if (isDuplicate) {
        showAlert("warning", `Package "${newPkg.name}" already exists`);
      }
      return !isDuplicate;
    });

    if (filteredCombinations.length > 0) {
      setCombinations((prev) => [...prev, ...filteredCombinations]);
    }

    packageForm.setValue("carTypes", []);
    packageForm.setValue("terminals", []);
  };

  const removeCombination = (id) => {
    // Find index of item to remove
    const indexToRemove = combinations.findIndex(item => item.id === id);
    
    if (indexToRemove !== -1 && draftRates) {
      // Update draftRates: remove item at index, shift subsequent items down
      const newDraftRates = { ...draftRates };
      delete newDraftRates[indexToRemove];
      
      // Shift keys down
      Object.keys(newDraftRates).forEach(key => {
        const numKey = Number(key);
        if (numKey > indexToRemove) {
          newDraftRates[numKey - 1] = newDraftRates[numKey];
          delete newDraftRates[numKey];
        }
      });
      
      setDraftRates(newDraftRates);
    }
    setCombinations((prev) => prev.filter((item) => item.id !== id));
  };

  const toggleIsKiosk = (id) => {
    setCombinations((prev) =>
      prev.map((item) => (item.id === id ? { ...item, isKiosk: !item.isKiosk } : item))
    );
  };

  const toggleIsActive = (id) => {
    setCombinations((prev) =>
      prev.map((item) => (item.id === id ? { ...item, isActive: !item.isActive } : item))
    );
  };

  // Check which clients don't have any packages
  const getClientsWithoutPackages = () => {
    return createdClients.filter((client) => {
      const hasPackage = combinations.some((pkg) => pkg.corporateClientId === client.id);
      return !hasPackage;
    });
  };

  // Handle proceeding to rates with validation
  const handleProceedToRates = () => {
    if (combinations.length === 0) {
      showAlert("warning", "Please add at least one package combination");
      return;
    }
    
    // Check if any clients don't have packages (skip if user opted out)
    if (!skipClientWarning) {
      const clientsWithoutPkgs = getClientsWithoutPackages();
      if (clientsWithoutPkgs.length > 0) {
        setClientsWithoutPackages(clientsWithoutPkgs);
        setShowClientWarning(true);
        return;
      }
    }
    
    setHasVisitedTab3(true);
    setActiveTab(tabIndex.rates); // Go to Rate Creation tab
  };

  // Handler for PackageRateCreateForm onSubmit
  const handlePackagesBulkCreate = async (packagesDTO) => {
    const response = await createPackagesBulk(packagesDTO);
    if (response?.status === 201) {
      showAlert("success", `Created ${packagesDTO.length} package(s) with rates successfully!`);
    }
  };

  // Handle modal close with warning
  const handleCloseWithWarning = () => {
    // Show warning only if clients have been created
    if (createdClients.length > 0) {
      setShowCloseWarning(true);
      return;
    }
    // No clients created, close directly
    if (handleCloseModal) handleCloseModal();
  };

  // Register close warning handler with parent
  useEffect(() => {
    if (registerCloseHandler) {
      registerCloseHandler(handleCloseWithWarning);
    }
  }, [createdClients.length]);

  // Handle tab change - backward always allowed, forward validated
  const handleTabChange = (e, newValue) => {
    setActiveTab(newValue);
  };

  // Confirm close
  const confirmClose = () => {
    setShowCloseWarning(false);
    if (handleCloseModal) handleCloseModal();
  };

  // Client options for Tab 2 dropdown
  const clientOptions = createdClients.map((client) => ({
    value: client,
    label: `${client.companyName} - ${client.serviceCityName}`,
  }));

  const { isCityAllowed } = usePayoutAllowedCities();
  const showDriverOptions = globalCityPreselected && isCityAllowed;

  // Build dynamic tab list — Driver Payout/Credit are tabs 2 & 3 when enabled
  const tabDefs = (() => {
    const defs = [{ key: "client", label: "1. Create Client" }];
    if (showDriverOptions && enableDriverPayout)  defs.push({ key: "driverPayout", label: `${defs.length + 1}. Driver Payout` });
    if (showDriverOptions && enableDriverCredit)  defs.push({ key: "driverCredit", label: `${defs.length + 1}. Driver Credit` });
    defs.push({ key: "packages", label: `${defs.length + 1}. Create Packages` });
    defs.push({ key: "rates",    label: `${defs.length + 1}. Create Rates` });
    return defs;
  })();
  const tabIndex = Object.fromEntries(tabDefs.map((t, i) => [t.key, i]));

  return (
    <div>
      {/* Tab Header */}
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
      >
        {tabDefs.map((tab) => (
          <Tab
            key={tab.key}
            label={tab.label}
            disabled={
              tab.key === "packages"    ? createdClients.length === 0 :
              tab.key === "rates"       ? !hasVisitedTab3 :
              tab.key === "driverPayout"? createdClients.length === 0 :
              tab.key === "driverCredit"? createdClients.length === 0 :
              false
            }
          />
        ))}
      </Tabs>

      {/* TAB 1: Create Client */}
      {activeTab === tabIndex.client && (
        <>
          {createdClients.length > 0 && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                backgroundColor: '#e3f2fd',
                border: '1px solid #1976d2',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  backgroundColor: '#1976d2',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '16px',
                }}
              >
                i
              </Box>
              <Typography variant="body1" sx={{ color: '#1565c0', fontWeight: 500 }}>
                Select a Service City to update an existing client or create a new one.
              </Typography>
            </Box>
          )}
          <Form onSubmit={clientForm.handleSubmit(handleClientSubmit)}>
          <Row>
            <FormGroup className="col-lg-3">
              <Label className="col-lg-12 col-form-label">Company Name *</Label>
              <Col lg="12">
                <input
                  className="form-control"
                  type="text"
                  maxLength={100}
                  placeholder="Enter company name"
                  {...clientForm.register("companyName", {
                    required: { value: true, message: "Company Name is required" },
                  })}
                />
                <span style={{ color: "red" }}>{clientForm.formState.errors?.companyName?.message}</span>
              </Col>
            </FormGroup>

            <FormGroup className="col-lg-3">
              <Label className="col-lg-12 col-form-label">
                {createdClients.length > 0 ? "Service City *" : "Service Cities *"}
              </Label>
              <Col lg="12">
                {createdClients.length > 0 ? (
                  // EDIT MODE: Single select
                  <Select
                    className="basic-select"
                    classNamePrefix="select"
                    value={
                      clientForm.watch("serviceCity")
                        ? cityData.find((c) => c.value?.id === clientForm.watch("serviceCity")?.id)
                        : null
                    }
                    onChange={handleSingleCityChange}
                    placeholder="Select City to Update"
                    options={cityData}
                    isClearable
                    isDisabled={globalCityPreselected}
                    getOptionValue={(option) => option.value?.id}
                  />
                ) : (
                  // CREATE MODE: Multi select (or single look when preselected from global context)
                  <Select
                    {...(!globalCityPreselected && {
                      isMulti: true,
                      closeMenuOnSelect: false,
                      hideSelectedOptions: true,
                    })}
                    noOptionsMessage={() => null}
                    className="basic-multi-select"
                    classNamePrefix="select"
                    value={globalCityPreselected
                      ? (serviceCitiesField.value?.[0] ?? null)
                      : serviceCitiesField.value}
                    onChange={handleCitiesChange}
                    placeholder="Select Cities"
                    options={cityData}
                    getOptionValue={(option) => option.value?.id}
                    isDisabled={globalCityPreselected}
                  />
                )}
                <span style={{ color: "red" }}>
                  {createdClients.length > 0
                    ? clientForm.formState.errors?.serviceCity?.message
                    : clientForm.formState.errors?.serviceCities?.message}
                </span>
              </Col>
            </FormGroup>



            <FormGroup className="col-lg-3">
              <Label className="col-lg-12 col-form-label">Contact Person *</Label>
              <Col lg="12">
                <input
                  className="form-control"
                  type="text"
                  maxLength={100}
                  {...clientForm.register("contactPerson", {
                    required: { value: true, message: "Contact Person is required" },
                  })}
                />
                <span style={{ color: "red" }}>{clientForm.formState.errors?.contactPerson?.message}</span>
              </Col>
            </FormGroup>

            <FormGroup className="col-lg-3">
              <Label className="col-lg-12 col-form-label">Mobile Number *</Label>
              <Col lg="12">
                <input
                  type="text"
                  className="form-control"
                  maxLength={10}
                  value={mobileField.value}
                  pattern="[0-9]+"
                  onChange={(e) => mobileField.onChange(e.target.value.replace(/[^0-9]/g, ""))}
                />
                <span style={{ color: "red" }}>{clientForm.formState.errors?.mobile?.message}</span>
              </Col>
            </FormGroup>

            <FormGroup className="col-lg-3">
              <Label className="col-lg-12 col-form-label">Email *</Label>
              <Col lg="12">
                <input
                  className="form-control"
                  type="text"
                  maxLength={100}
                  {...clientForm.register("email", {
                    required: { value: true, message: "Email is required" },
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                      message: "Please enter a valid email!",
                    },
                  })}
                />
                <span style={{ color: "red" }}>{clientForm.formState.errors?.email?.message}</span>
              </Col>
            </FormGroup>

            <FormGroup className="col-lg-3">
              <Label className="col-lg-12 col-form-label">Address *</Label>
              <Col lg="12">
                <input
                  className="form-control"
                  type="text"
                  maxLength={1024}
                  {...clientForm.register("addressLine", {
                    required: { value: true, message: "Address is required" },
                  })}
                />
                <span style={{ color: "red" }}>{clientForm.formState.errors?.addressLine?.message}</span>
              </Col>
            </FormGroup>

            <FormGroup className="col-lg-3">
              <Label className="col-lg-12 col-form-label">City *</Label>
              <Col lg="12">
                <input
                  className="form-control"
                  type="text"
                  maxLength={30}
                  {...clientForm.register("city", {
                    required: { value: true, message: "City is required" },
                  })}
                />
                <span style={{ color: "red" }}>{clientForm.formState.errors?.city?.message}</span>
              </Col>
            </FormGroup>

            <FormGroup className="col-lg-3">
              <Label className="col-lg-12 col-form-label">State *</Label>
              <Col lg="12">
                <input
                  className="form-control"
                  type="text"
                  maxLength={30}
                  {...clientForm.register("state", {
                    required: { value: true, message: "State is required" },
                  })}
                />
                <span style={{ color: "red" }}>{clientForm.formState.errors?.state?.message}</span>
              </Col>
            </FormGroup>
            <FormGroup className="col-lg-3">
              <Label className="col-lg-12 col-form-label">Tax *</Label>
              <Col lg="12">
                <Select
                  value={clientForm.watch("overallTax")}
                  onChange={handleTaxChange}
                  placeholder="Select Tax"
                  options={combinedTax}
                />
                <span style={{ color: "red" }}>{clientForm.formState.errors?.overallTax?.message}</span>
              </Col>
            </FormGroup>
              {/* Driver Payout / Driver Credit checkboxes — only for Delhi/city 1 or 4 */}
              {showDriverOptions && (
                <FormGroup className="col-lg-6">
                  <Col lg="12" style={{ display: "flex", gap: "24px", alignItems: "center", paddingTop: "20px", marginTop: "20px" }}>
                    {(() => {
                      const isEditMode = createdClients.length > 0;
                      return [
                        { label: "Driver Payout", checked: enableDriverPayout, onChange: (v) => setEnableDriverPayout(v) },
                        { label: "Driver Credit", checked: enableDriverCredit, onChange: (v) => setEnableDriverCredit(v) },
                      ].map(({ label, checked, onChange }) => (
                        <label
                          key={label}
                          title={isEditMode ? "Cannot change after clients are created" : undefined}
                          style={{
                            display: "flex", alignItems: "center", gap: "8px",
                            cursor: isEditMode ? "not-allowed" : "pointer",
                            marginBottom: 0,
                            opacity: isEditMode ? 0.5 : 1,
                            pointerEvents: isEditMode ? "none" : "auto",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => onChange(e.target.checked)}
                            disabled={isEditMode}
                            style={{ position: "absolute", opacity: 0, width: 0, height: 0 }}
                          />
                          <span style={{
                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                            width: 18, height: 18, borderRadius: 3, flexShrink: 0,
                            border: checked ? "none" : "2px solid #aaa",
                            backgroundColor: checked ? (isEditMode ? "#6abf7b" : "#11BF5C") : "#fff",
                            transition: "background 0.15s",
                          }}>
                            {checked && (
                              <svg width="11" height="9" viewBox="0 0 11 9" fill="none">
                                <path d="M1 4L4 7.5L10 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            )}
                          </span>
                          <span style={{ fontSize: 14 }}>{label}</span>
                        </label>
                      ));
                    })()}
                  </Col>
                </FormGroup>
              )}
          </Row>

          {/* Advanced Configurations Toggle */}
          <Row>
            <div
              style={{
                color: "#11BF5C",
                textDecoration: "underline",
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
              }}
              onClick={() => setIsAdvanced(!isAdvanced)}
            >
              Advanced Configurations{" "}
              {isAdvanced ? (
                <ArrowUp style={{ width: "15px", height: "15px", marginLeft: "5px" }} />
              ) : (
                <ArrowDown style={{ width: "15px", height: "15px", marginLeft: "5px" }} />
              )}
            </div>
          </Row>

          {/* Advanced Configurations Fields */}
          {isAdvanced && (
            <Row>
              <FormGroup className="col-lg-3">
                <Label className="col-lg-12 col-form-label">Pan Number</Label>
                <Col lg="12">
                  <input className="form-control" type="text" maxLength={100} {...clientForm.register("panNo")} />
                </Col>
              </FormGroup>
              <FormGroup className="col-lg-3">
                <Label className="col-lg-12 col-form-label">Service Tax Number</Label>
                <Col lg="12">
                  <input className="form-control" type="text" maxLength={100} {...clientForm.register("serviceTaxNo")} />
                </Col>
              </FormGroup>
              <FormGroup className="col-lg-3">
                <Label className="col-lg-12 col-form-label">Fax</Label>
                <Col lg="12">
                  <input className="form-control" type="text" maxLength={15} {...clientForm.register("fax")} />
                </Col>
              </FormGroup>
              <FormGroup className="col-lg-3">
                <Label className="col-lg-12 col-form-label">Pincode</Label>
                <Col lg="12">
                  <input
                    className="form-control"
                    type="text"
                    maxLength={6}
                    {...clientForm.register("pincode", {
                      onChange: (e) => {
                        clientForm.setValue("pincode", e.target.value.replace(/[^0-9]/g, ""));
                      },
                    })}
                  />
                </Col>
              </FormGroup>
              <FormGroup className="col-lg-3">
                <Label className="col-lg-12 col-form-label">Billing Address</Label>
                <Col lg="12">
                  <input className="form-control" type="text" maxLength={1024} {...clientForm.register("billingAddress")} />
                </Col>
              </FormGroup>
              <FormGroup className="col-lg-3">
                <Label className="col-lg-12 col-form-label">Billing City</Label>
                <Col lg="12">
                  <input className="form-control" type="text" maxLength={30} {...clientForm.register("billingCity")} />
                </Col>
              </FormGroup>
              <FormGroup className="col-lg-3">
                <Label className="col-lg-12 col-form-label">Billing State</Label>
                <Col lg="12">
                  <input className="form-control" type="text" maxLength={30} {...clientForm.register("billingState")} />
                </Col>
              </FormGroup>
              <FormGroup className="col-lg-3">
                <Label className="col-lg-12 col-form-label">Billing Pincode</Label>
                <Col lg="12">
                  <input
                    className="form-control"
                    type="text"
                    maxLength={6}
                    {...clientForm.register("billingPincode", {
                      onChange: (e) => {
                        clientForm.setValue("billingPincode", e.target.value.replace(/[^0-9]/g, ""));
                      },
                    })}
                  />
                </Col>
              </FormGroup>
              <FormGroup className="col-lg-3">
                <Label className="col-lg-12 col-form-label">Billing Contact Person</Label>
                <Col lg="12">
                  <input className="form-control" type="text" maxLength={100} {...clientForm.register("billingContactPerson")} />
                </Col>
              </FormGroup>
              <FormGroup className="col-lg-3">
                <Label className="col-lg-12 col-form-label">Billing Contact Mobile</Label>
                <Col lg="12">
                  <input
                    type="text"
                    className="form-control"
                    maxLength={10}
                    {...clientForm.register("billingContactMobile", {
                      onChange: (e) => {
                        clientForm.setValue("billingContactMobile", e.target.value.replace(/[^0-9]/g, ""));
                      },
                    })}
                  />
                </Col>
              </FormGroup>
            </Row>
          )}

          <div style={{ display: "flex", justifyContent: "center", marginTop: "20px", gap: "15px" }}>
            {createdClients.length > 0 ? (
              // EDIT MODE: Split buttons
              <>
                <button
                  type="submit"
                  className="btn"
                  disabled={isCreatingClient}
                  style={{
                    backgroundColor: isCreatingClient ? "#ccc" : "rgba(57, 181, 74, 1)",
                    color: "white",
                    border: "none",
                    padding: "10px 30px",
                    borderRadius: "6px",
                    cursor: isCreatingClient ? "not-allowed" : "pointer",
                  }}
                >
                  {(() => {
                    const selectedCity = clientForm.watch("serviceCity");
                    const existingClient = selectedCity && createdClients.some((c) => c.serviceCityId === selectedCity.id);
                    
                    if (isCreatingClient) {
                      return existingClient ? "Updating..." : "Creating...";
                    }
                    
                    return existingClient ? "Update" : "Create";
                  })()}
                </button>
                <button
                  type="button"
                  className="btn"
                  disabled={isCreatingClient}
                  onClick={() => setActiveTab(
                    showDriverOptions && enableDriverPayout ? tabIndex.driverPayout :
                    showDriverOptions && enableDriverCredit ? tabIndex.driverCredit :
                    tabIndex.packages
                  )}
                  style={{
                    backgroundColor: isCreatingClient ? "#ccc" : "#007bff",
                    color: "white",
                    border: "none",
                    padding: "10px 30px",
                    borderRadius: "6px",
                    cursor: isCreatingClient ? "not-allowed" : "pointer",
                  }}
                >
                  Continue →
                </button>
              </>
            ) : (
              // CREATE MODE: Single button
              <button
                type="submit"
                className="btn"
                disabled={isCreatingClient}
                style={{
                  backgroundColor: isCreatingClient ? "#ccc" : "rgba(57, 181, 74, 1)",
                  color: "white",
                  border: "none",
                  padding: "10px 30px",
                  borderRadius: "6px",
                  cursor: isCreatingClient ? "not-allowed" : "pointer",
                }}
              >
                {isCreatingClient ? "Creating..." : "Create Clients & Continue"}
              </button>
            )}
          </div>
        </Form>
        </>
      )}

      {/* TAB 2: Bulk Create Packages */}
      {activeTab === tabIndex.packages && (
        <>
          <Form onSubmit={packageForm.handleSubmit(addCombinations)}>
            <Row>
              <FormGroup className="col-lg-4">
                <Label className="col-lg-12 col-form-label">Corporate Client</Label>
                <Col lg="12">
                  <Select
                    value={watchSelectedClient ? clientOptions.find((o) => o.value.id === watchSelectedClient.id) : null}
                    onChange={handleClientSelect}
                    placeholder="Select Client"
                    options={clientOptions}
                    isClearable
                  />
                  <span style={{ color: "red" }}>{packageForm.formState.errors?.selectedClient?.message}</span>
                </Col>
              </FormGroup>

              {watchSelectedClient && (
                <>
              <FormGroup className="col-lg-4">
                <Label className="col-lg-12 col-form-label">Service Type *</Label>
                <Col lg="12">
                  <Select
                    value={watchServiceType}
                    onChange={handleServiceTypeSelect}
                    placeholder="Select Service Type"
                    options={serviceTypeOptions}
                    isClearable
                  />
                  <span style={{ color: "red" }}>{packageForm.formState.errors?.serviceType?.message}</span>
                </Col>
              </FormGroup>
                  <FormGroup className="col-lg-2">
                    <Label className="col-lg-12 col-form-label">City</Label>
                    <Col lg="12">
                      <input className="form-control" type="text" value={watchSelectedClient.serviceCityName || "N/A"} disabled style={{ backgroundColor: "#f5f5f5" }} />
                    </Col>
                  </FormGroup>
                </>
              )}

              <FormGroup className="col-lg-4">
                <Label className="col-lg-12 col-form-label">Package Name</Label>
                <Col lg="12">
                  <input
                    className="form-control"
                    type="text"
                    placeholder="Enter package name"
                    maxLength={15}
                    {...packageForm.register("packageName", { required: { value: true, message: "Package Name is required" } })}
                  />
                  <span style={{ color: "red" }}>{packageForm.formState.errors?.packageName?.message}</span>
                </Col>
              </FormGroup>

              <FormGroup className="col-lg-4">
                <Label className="col-lg-12 col-form-label">Car Types</Label>
                <Col lg="12">
                  <Select
                    isMulti
                    closeMenuOnSelect={false}
                    hideSelectedOptions={true}
                    noOptionsMessage={() => null}
                    className="basic-multi-select"
                    classNamePrefix="select"
                    value={carTypesField.value}
                    onChange={(opt) => carTypesField.onChange(opt || [])}
                    placeholder={watchSelectedClient ? "Select Car Types" : "Select client first"}
                    options={carTypeData}
                    isDisabled={!watchSelectedClient}
                    getOptionValue={(option) => option.value?.id || option.value}
                  />
                  <span style={{ color: "red" }}>{packageForm.formState.errors?.carTypes?.message}</span>
                </Col>
              </FormGroup>

              {showTerminals && (
                <FormGroup className="col-lg-4">
                  <Label className="col-lg-12 col-form-label">Terminals</Label>
                  <Col lg="12">
                    <Select
                      isMulti
                      closeMenuOnSelect={false}
                      hideSelectedOptions={true}
                      noOptionsMessage={() => null}
                      className="basic-multi-select"
                      classNamePrefix="select"
                      value={terminalsField.value}
                      onChange={(opt) => terminalsField.onChange(opt || [])}
                      placeholder="Select Terminals"
                      options={terminals}
                      getOptionValue={(option) => option.value}
                    />
                    <span style={{ color: "red" }}>{packageForm.formState.errors?.terminals?.message}</span>
                  </Col>
                </FormGroup>
              )}
            </Row>

            {/* Advanced Configurations */}
            <Row>
              <div
                style={{ color: "#11BF5C", textDecoration: "underline", display: "flex", alignItems: "center", cursor: "pointer", marginLeft: "12px", marginTop: "10px" }}
                onClick={() => setIsAdvanced(!isAdvanced)}
              >
                Advanced Configurations {isAdvanced ? <ArrowUp style={{ width: "15px", height: "15px", marginLeft: "5px" }} /> : <ArrowDown style={{ width: "15px", height: "15px", marginLeft: "5px" }} />}
              </div>
            </Row>

            {isAdvanced && (
              <Row style={{ marginTop: "15px" }}>
                <FormGroup className="col-lg-6">
                  <Label className="col-lg-12 col-form-label">Description</Label>
                  <Col lg="12">
                    <input className="form-control" type="text" placeholder="Enter description" {...packageForm.register("description")} />
                  </Col>
                </FormGroup>
                <FormGroup className="col-lg-6">
                  <Label className="col-lg-12 col-form-label">Available From</Label>
                  <Col lg="12">
                    <InputGroup className="date" data-target-input="nearest">
                      <DatePicker
                        className="form-control datetimepicker-input digits"
                        selected={applicableFromDateField.value}
                        showTimeSelect
                        showYearDropdown
                        showMonthDropdown
                        scrollableYearDropdown
                        onChange={applicableFromDateField.onChange}
                        dateFormat="MMMM d yyyy, h:mm:ss a"
                        onKeyDown={(e) => e.preventDefault()}
                      />
                    </InputGroup>
                  </Col>
                </FormGroup>
                <FormGroup className="col-lg-6">
                  <Label className="col-lg-12 col-form-label">Available To</Label>
                  <Col lg="12">
                    <InputGroup className="date" data-target-input="nearest">
                      <DatePicker
                        className="form-control datetimepicker-input digits"
                        selected={applicableToDateField.value}
                        onChange={applicableToDateField.onChange}
                        showTimeSelect
                        showYearDropdown
                        showMonthDropdown
                        scrollableYearDropdown
                        dateFormat="MMMM d yyyy, h:mm:ss a"
                        onKeyDown={(e) => e.preventDefault()}
                        minDate={applicableFromDateField.value}
                      />
                    </InputGroup>
                  </Col>
                </FormGroup>
              </Row>
            )}

            <div style={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
              <button
                type="submit"
                className="btn"
                disabled={!watchSelectedClient}
                style={{
                  backgroundColor: watchSelectedClient ? "rgba(57, 181, 74, 1)" : "#ccc",
                  color: "white",
                  border: "none",
                  padding: "10px 30px",
                  borderRadius: "6px",
                  cursor: watchSelectedClient ? "pointer" : "not-allowed",
                }}
              >
                Add
              </button>
            </div>
          </Form>

          {/* Combinations Table */}
          {combinations.length > 0 && (
            <Box sx={{ marginTop: "30px" }}>
              <Typography variant="h6" sx={{ marginBottom: "15px", fontWeight: 600 }}>
                Packages ({combinations.length})
              </Typography>
              <CombinationTable
                combinations={combinations}
                onToggleKiosk={toggleIsKiosk}
                onToggleActive={toggleIsActive}
                onRemove={removeCombination}
                showClient
                showCity
                showServiceType
                showCarType
                showTerminal
              />

              <div style={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
                <button
                  type="button"
                  className="btn"
                  onClick={handleProceedToRates}
                  style={{
                    backgroundColor: "rgba(57, 181, 74, 1)",
                    color: "white",
                    border: "none",
                    padding: "10px 40px",
                    borderRadius: "6px",
                    fontSize: "16px",
                  }}
                >
                  Proceed to Rates →
                </button>
              </div>
            </Box>
          )}
        </>
      )}

      {/* TAB 3: Rate Creation */}
      {activeTab === tabIndex.rates && (
        <PackageRateCreateForm
          packages={combinations}
          initialRates={draftRates}
          onRatesChange={setDraftRates}
          onSubmit={handlePackagesBulkCreate}
          onComplete={() => {
            if (handleCloseModal) handleCloseModal();
          }}
          onBack={() => setActiveTab(tabIndex.packages)}
        />
      )}

      {/* TAB: Driver Payout */}
      {showDriverOptions && enableDriverPayout && activeTab === tabIndex.driverPayout && (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {createdClients.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mt: 4 }}>
              No clients created yet. Please complete Tab 1 first.
            </Typography>
          ) : (
            createdClients.map((client) => (
              <Box key={client.id} sx={{ mb: 4 }}>
                {createdClients.length > 1 && (
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    {client.companyName} — {client.serviceCityName}
                  </Typography>
                )}
                <DriverPayoutsTable
                  corporateClientId={client.id}
                  serviceCityId={client.serviceCityId}
                />
              </Box>
            ))
          )}

          {/* Navigation footer */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mb: 2, gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => setActiveTab(tabIndex.client)}
              sx={{ borderColor: "#666", color: "#666" }}
            >
              ← Back
            </Button>
            <Button
              variant="contained"
              onClick={() => setActiveTab(
                showDriverOptions && enableDriverCredit ? tabIndex.driverCredit : tabIndex.packages
              )}
              sx={{ backgroundColor: "#007bff", "&:hover": { backgroundColor: "#0056b3" } }}
            >
              Next →
            </Button>
          </Box>
        </Box>
      )}

      {/* TAB: Driver Credit */}
      {showDriverOptions && enableDriverCredit && activeTab === tabIndex.driverCredit && (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {createdClients.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mt: 4 }}>
              No clients created yet. Please complete Tab 1 first.
            </Typography>
          ) : (
            createdClients.map((client) => (
              <Box key={client.id} sx={{ mb: 4 }}>
                {createdClients.length > 1 && (
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    {client.companyName} — {client.serviceCityName}
                  </Typography>
                )}
                <DriverCreditsTable
                  corporateClientId={client.id}
                />
              </Box>
            ))
          )}

          {/* Navigation footer */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mb: 2, gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => setActiveTab(
                showDriverOptions && enableDriverPayout ? tabIndex.driverPayout : tabIndex.client
              )}
              sx={{ borderColor: "#666", color: "#666" }}
            >
              ← Back
            </Button>
            <Button
              variant="contained"
              onClick={() => setActiveTab(tabIndex.packages)}
              sx={{ backgroundColor: "#007bff", "&:hover": { backgroundColor: "#0056b3" } }}
            >
              Next →
            </Button>
          </Box>
        </Box>
      )}

      {/* Warning Dialog: Clients without packages */}
      <Dialog open={showClientWarning} onClose={() => setShowClientWarning(false)}>
        <DialogTitle>Warning</DialogTitle>
        <DialogContent>
          <Typography>
            The following clients don't have any packages:
          </Typography>
          <ul style={{ marginTop: '10px' }}>
            {clientsWithoutPackages.map((client) => (
              <li key={client.id}>
                {client.companyName} - {client.serviceCityName}
              </li>
            ))}
          </ul>
          <Typography sx={{ mt: 2 }}>
            Do you want to proceed anyway?
          </Typography>
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
            <Checkbox
              checked={skipClientWarning}
              onChange={(e) => setSkipClientWarning(e.target.checked)}
              sx={{ color: '#39B54A', '&.Mui-checked': { color: '#39B54A' } }}
            />
            <Typography variant="body2">Do not show this warning again</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowClientWarning(false)} sx={{ color: '#666' }}>
            Cancel
          </Button>
          <Button 
            onClick={() => {
              setShowClientWarning(false);
              setHasVisitedTab3(true);
              setActiveTab(tabIndex.rates);
            }} 
            variant="contained" 
            sx={{ backgroundColor: '#39B54A', '&:hover': { backgroundColor: '#2d8f3a' } }}
          >
            Proceed Anyway
          </Button>
        </DialogActions>
      </Dialog>

      {/* Warning Dialog: Close confirmation */}
      <Dialog open={showCloseWarning} onClose={() => setShowCloseWarning(false)}>
        <DialogTitle>Confirm Close</DialogTitle>
        <DialogContent>
          <Typography>
            Corporate clients have already been created. If you close now, the clients will remain without packages or rates.
          </Typography>
          <Typography sx={{ mt: 2, fontWeight: 500 }}>
            Are you sure you want to leave?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCloseWarning(false)} sx={{ color: '#666' }}>
            Cancel
          </Button>
          <Button 
            onClick={confirmClose} 
            variant="contained" 
            sx={{ backgroundColor: '#dc3545', '&:hover': { backgroundColor: '#c82333' } }}
          >
            Leave
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default CorpBulkCreate;
